import React, {useState} from "react";
import { compose, withHandlers, withStateHandlers } from "recompose";
import { Link } from "react-router-dom";
import {
  Page,
  withTable,
  withPopover,
  PendingButton,
  withLocalForm,
  LoadingMsg,
} from "components/common";
import { withXeroErrors } from "components/withXeroErrors";
import { Trans, useTranslation } from "react-i18next";
import { Control } from "react-redux-form";
import { Table, Button, PopoverBody, PopoverHeader } from "reactstrap";
import { keys } from "lodash";
import { formatCurrency, formatNumber } from "services/util";
import { NotificationManager } from "react-notifications";
import * as XLSX from 'xlsx';
import ImportModal, { readFileAsync } from "components/ImportModal";
import { XeroClient } from "services";
import { createPortal } from "react-dom";
import { itemsNotes as importantNotes } from "pages/UserList/modelNames";
import { useUserContext } from "hooks";

const Search = withLocalForm()(
  ({ LocalForm, className, style, onFiltersChanged, onSpecificData, item}) => {

    const { t } = useTranslation()

    const [noItemsFound, setNoItemsFound] = useState(false);

    React.useEffect(() => {
      if (item.results.length === 0 && !item.loading) {
        setNoItemsFound(true);
        onSpecificData('No items found for the current search.');
      } else {
        setNoItemsFound(false);
        onSpecificData('');
      }
    }, [item, onSpecificData]);

    return (
    <LocalForm
      className={className}
      style={style}
      onChange={({ search }) => {
        search = (search || "").replace(/"/g, "").toLowerCase();

        if (search) {
          onFiltersChanged({
            raw: `((Code != null AND Code.ToLower().Contains("${search}")) OR (Name != null AND Name.ToLower().Contains("${search}")))`,
            search: search,
          });
        } else {
          onFiltersChanged({});
        }
      }}
    >
      <div className="input-group my-3">
        <Control.text
          model=".search"
          debounce={1000}
          className="form-control w-100"
          placeholder={t("Search...")}
        />
         {/*<div className="input-group-append">
          { ae update add fontAwesome style }
          <span className="fa fa-search input-group-text"  style={{fontFamily: 'FontAwesome, Arial', fontStyle: 'normal'}}/>
        </div>*/}
      </div>
    </LocalForm>
  )
  }
);

export const InventoryList = ({
  match,
  XeroErrors,
  Popover,
  pending,
  handleDelete,
  togglePopover,
  numSelected,
  TableContainer,
  setFilters,
  Loader,
  data,
  XeroTableDataFetch,
  Pagination,
  ViewingRecords,
  noItemsFound,
  ...props
}) => {

  const [isLoading, setIsLoading] = useState(false)
  const [specificData, setSpecificData] = useState(null);
  const xero = XeroClient();
  const {t} = useTranslation()

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);

  const user = useUserContext();
  const can_view_cost = user?.permissions.includes('view_inventory_cost');

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };


  const handleSpecificData = (data) => {
    setSpecificData(data);
  };

  const refreshData = () => {
    setForceRefresh((prev) => !prev);
  };

  const uploadFile = async (file) => {
    try {
      const content = await readFileAsync(file);
      setIsLoading(true)
      const parsedData = parseXlsx(content);
      const filteredData = parsedData.filter((row) => {
        const itemCode = row.Code?.trim();

        return itemCode
      });
      const response = await xero.post('items', filteredData);
      setIsLoading(false)
      NotificationManager.success(t('Items uploaded successfully'));
      console.log('Data successfully:', response);

      setTimeout(() => {
        setIsModalOpen(false);
        refreshData()
      }, 800);
    } catch (error) {  
      setIsLoading(false)
      let errorCount = 0;

      if (error && error.response && error.response.data && error.response.data.detail) {
        error.response.data.detail.forEach((detail, index) => {
          if (errorCount < 3) {
            const errorMessage = `${index + 1}. ${detail}`;
            NotificationManager.error(errorMessage, 'Error');
            console.error(errorMessage);
            errorCount++;
          }
        });
      } else {
        if (errorCount < 3) {
          const errorMessage = t('Error uploading file, check console for more info.');
          NotificationManager.error(errorMessage);
          console.error(errorMessage);
          errorCount++;
        }
      }
    }
  };
  
  const parseXlsx = (xlsxContent) => {
    const data = new Uint8Array(xlsxContent);
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const jsonData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 });
  
    const headerRow = jsonData[0];
  
    return jsonData.slice(1).map((row) => {
      const getValue = (columnName) => row[headerRow.indexOf(columnName)];
  
      const saleValue = getValue("SellingPrice");
      const purchaseValue = getValue("BuyingPrice");
  
      return {
        Code: getValue("ItemCode*"),
        Name: getValue("ItemName"),
        Description: getValue("DescriptionSales"),
        IsSold: saleValue ? true : false,
        IsPurchased: purchaseValue ? true : false,
        ...(saleValue && { SalesDetails: [{ UnitPrice: saleValue, AccountCode: getValue("SalesAccountCode") }] }),
        PurchaseDescription: getValue("DescriptionPurchase"),
        ...(purchaseValue && { PurchaseDetails: [{ UnitPrice: purchaseValue, AccountCode: getValue("PurchaseAccountCode") }] }),
      };
    });
  };

  return (
  <Page title="Inventory">
     {isLoading &&
      createPortal(
      <div className="content">
        <div className="loader"></div>
          {<LoadingMsg/> }
        </div>,
      document.querySelector('#loader-holder')
    )}
    <Search onFiltersChanged={setFilters} style={{ marginRight: "8px"}} onSpecificData={handleSpecificData} item={data}/> 
    <div className="d-flex mb-2 ">
      {/* ae update */}
      <Link to={`${match.url}/new`} className="btn btn-success">
        <i className="fa fa-plus mr-2"/>
        <Trans>New Item</Trans>
      </Link>
      <Button color="primary" onClick={openModal}>
        <i className="fas fa-file-excel mr-2"/><Trans>Import Items</Trans>
      </Button>
      <div>
        <ImportModal
          isOpen={isModalOpen}
          toggle={closeModal}
          title="Items"
          importantNotes={importantNotes}
          handleUpload={uploadFile} 
        />
      </div>
      <div className="mx-auto"/>
      <div>
        <PendingButton
          pending={pending}
          id="delete-button"
          onClick={togglePopover}
          disabled={!numSelected || pending}
          color="danger"
        ><i className="fa fa-trash mr-2"/>
          <Trans>Delete</Trans>
        </PendingButton>
        <Popover target="#delete-button">
          <PopoverHeader className="text-center">
            <Trans>Are you sure you want to delete the selected rows?</Trans>
          </PopoverHeader>
          <PopoverBody className="d-flex justify-content-between">
            <Button size="sm" onClick={togglePopover}>
              <Trans>Cancel</Trans>
            </Button>
            <PendingButton
              pending={pending}
              color="success"
              size="sm"
              onClick={handleDelete}
            >
              <Trans>Confirm</Trans>
            </PendingButton>
          </PopoverBody>
        </Popover>
      </div>
    </div>
    <div>
      <XeroErrors color="warning" />
      
      <div className="nw-invoiceStyle inv-table">
        <TableContainer idKey="ItemID">
          <Table responsive>
            <thead>
              <tr>
                <th select />
                <th sortBy="Code" style={{}} className="text-nowrap text-left">
                  <Trans>Item Code</Trans>
                </th>
                <th sortBy="Name" className="text-nowrap">
                  <Trans>Item Name</Trans>
                </th>
               {can_view_cost && <th sortBy="PurchaseDetails.UnitPrice" className="text-nowrap">
                  <Trans>Cost Price</Trans>
                </th>}
                <th sortBy="SalesDetails.UnitPrice" className="text-nowrap">
                  <Trans>Sale Price</Trans>
                </th>
                <th sortBy="QuantityOnHand" className="text-nowrap">
                  <Trans>Quantity</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              <Loader />
              {data.results.map((i, idx) => (
                <tr key={i.ItemID}>
                  <td select />
                  <td className="text-left">
                    <Link to={`${match.url}/${i.ItemID}`}>{i.Code}</Link>
                  </td>
                  <td>{i.Name}</td>
                 {can_view_cost && <td>
                    {i.PurchaseDetails
                      ? formatCurrency(i.PurchaseDetails.UnitPrice)
                      : null}
                  </td>}
                  <td>
                    {i.SalesDetails
                      ? formatCurrency(i.SalesDetails.UnitPrice)
                      : null}
                  </td>
                  <td>{formatNumber(i.QuantityOnHand)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
        {specificData && (
            <div style={{fontSize: "1.3em"}} className="text-center">
              {specificData}
              <hr/>
            </div>
          )}
      </div>
      <XeroTableDataFetch key={forceRefresh} />
    </div>
  </Page>
)};

export const enhance = compose(
  withXeroErrors(),
  withPopover(),
  withStateHandlers(
    {},
    {
      setPending: () => (pending) => ({ pending }),
    }
  ),
  withTable({ limit: 9999999, url: "items", idKey: "ItemID" }),
  withHandlers({
    handleDelete:
      ({ selected, xero, refreshData, setPending, t, togglePopover }) =>
      () => {
        setPending(true);
        const ids = keys(selected);
        let numFailed = 0;

        Promise.mapSeries(ids, (id) =>
          xero.delete(`items/${id}`).catch((err) => numFailed++)
        )
          .then(() => {
            if (numFailed) {
              NotificationManager.error(t("Items with transactions can’t be deleted"));
            } else {
              NotificationManager.success(t("Deleted Successfully"));
            }
          })
          .finally(() => {
            togglePopover()
            setPending(false);
            refreshData();
          });
      },
  })
);

export default enhance(InventoryList);
